import { useCallback } from 'react'
import type { JSX } from 'react/jsx-runtime'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useChamber } from '@/hooks/use-chamber'
import { supabase } from '@/lib/supabase'
import type {
  CaseSummaryRow,
  CaseUpdate,
  CasesProviderProps,
  ClientInsert,
  CreateCaseParams,
  UpdateCaseParams,
  UseCasesSimplifiedReturn,
} from '@/types'
import { casesContext } from './cases-context'

export const CasesProvider = ({
  children,
}: CasesProviderProps): JSX.Element => {
  const { user } = useAuth()
  const { chamber } = useChamber()
  const queryClient = useQueryClient()

  // Fetch cases query
  const {
    data: cases = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['cases', chamber?.id],
    queryFn: async (): Promise<CaseSummaryRow[]> => {
      if (!user || !chamber?.id) throw new Error('Authentication required')

      const { data, error } = await supabase
        .from('case_summary')
        .select('*')
        .eq('chamber_id', chamber.id)
        .order('created_at', { ascending: false })

      if (error) throw error

      return data
    },
    enabled: Boolean(user) && Boolean(chamber?.id),
  })

  // Create case mutation
  const createCaseMutation = useMutation({
    mutationFn: async ({
      caseData,
      clientData,
    }: CreateCaseParams): Promise<CaseSummaryRow> => {
      if (!user || !chamber?.id) throw new Error('Authentication required')

      // Create client if provided
      let clientId: string | null = caseData.client_id || null

      if (clientData && clientId === null) {
        const clientDataWithIds: ClientInsert = {
          ...clientData,
          chamber_id: chamber.id,
          user_id: user.id,
        }

        const options = clientData.email ? { onConflict: 'chamber_id,email' } : {}
        const { data: client, error: clientError } = await supabase
          .from('clients')
          .upsert(clientDataWithIds, options)
          .select()
          .single()

        if (clientError) throw new Error(clientError.message)

        clientId = client.id
      }

      // Create case
      const { data: newCase, error: caseError } = await supabase
        .from('cases')
        .insert({
          ...caseData,
          client_id: clientId,
          chamber_id: chamber.id,
          user_id: user.id,
        })
        .select()
        .single()

      if (caseError) throw caseError

      const { data: caseSummary, error: summaryError } = await supabase
        .from('case_summary')
        .select('*')
        .eq('id', newCase.id)
        .single()

      if (summaryError) throw summaryError

      return caseSummary
    },
    onSuccess: () => {
      // Invalidate and refetch cases
      void queryClient.invalidateQueries({ queryKey: ['cases', chamber?.id] })
    },
  })

  // Update case mutation
  const updateCaseMutation = useMutation({
    mutationFn: async ({ caseId, updates }: UpdateCaseParams): Promise<CaseSummaryRow> => {
      const { data: updatedCase, error } = await supabase
        .from('cases')
        .update(updates)
        .eq('id', caseId)
        .select()
        .single()

      if (error) throw error

      const { data: caseSummary, error: summaryError } = await supabase
        .from('case_summary')
        .select('*')
        .eq('id', updatedCase.id)
        .single()

      if (summaryError) throw summaryError

      return caseSummary
    },
    onSuccess: () => {
      // Invalidate and refetch cases
      void queryClient.invalidateQueries({ queryKey: ['cases', chamber?.id] })
    },
  })

  // Wrapper functions
  const createCase = useCallback(
    async (params: CreateCaseParams): Promise<CaseSummaryRow> => {
      return createCaseMutation.mutateAsync(params)
    },
    [createCaseMutation],
  )

  const updateCase = useCallback(
    async (caseId: string, updates: CaseUpdate): Promise<CaseSummaryRow> => {
      return updateCaseMutation.mutateAsync({ caseId, updates })
    },
    [updateCaseMutation],
  )

  const refresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  const value: UseCasesSimplifiedReturn = {
    cases,
    isLoading,
    error: error?.message,
    refresh,
    createCase,
    updateCase,
  }

  return <casesContext.Provider value={value}>{children}</casesContext.Provider>
}
